import React, { useState, useEffect, useRef, useCallback } from "react";
import { getZunoOptions } from "../components/zunopricing";
import { ZunoVehicle } from "../components/ZunoVisuals";
import { DESTINATION_MODE, shouldFetchDestinationSuggestions } from "./destinationState";
import { LuCheck, LuChevronRight, LuClock3, LuCreditCard, LuInfo, LuMapPin, LuShieldCheck, LuStar, LuX, LuZap } from "react-icons/lu";
import { isStoreScreenshotCapture } from "../screenshotSession";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { createPlacesSessionToken, isNativeIOSPlaces, nativePlaceDetails, nativePlacesAutocomplete } from "../services/nativePlacesService";

const _autocompleteCache = new Map();
const STORE_SCREENSHOT_PUBLIC_ORIGIN = { lat: -20.14355, lng: -44.89065 };
const DEFAULT_PLACES_CENTER = { lat: -26.4833, lng: -49.0667 };

async function buscarNominatimParaScreenshot(query) {
  const params = new URLSearchParams({
    format: "jsonv2",
    countrycodes: "br",
    "accept-language": "pt-BR",
    limit: "5",
    q: query,
  });
  const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
  const headers = {
    Accept: "application/json",
    "User-Agent": "ZunoMovel-AppStoreScreenshots/1.0 (https://www.zunomovel.com.br)",
  };

  if (Capacitor.isNativePlatform()) {
    const response = await CapacitorHttp.get({ url, headers });
    if (response.status < 200 || response.status >= 300) throw new Error(`Nominatim ${response.status}`);
    return typeof response.data === "string" ? JSON.parse(response.data) : response.data;
  }

  const response = await fetch(url, { headers: { Accept: headers.Accept } });
  if (!response.ok) throw new Error(`Nominatim ${response.status}`);
  return response.json();
}

// ─── CARROS PREMIUM SILHOUETTE ────────────────────────────────
// Legacy fallback retained while the shared vehicle asset migrates consumers.
// eslint-disable-next-line no-unused-vars
const CarMovel = ({ ativo }) => (
  <svg viewBox="0 0 120 56" width="96" height="45" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bodyM" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={ativo ? "#e2e8f0" : "#94a3b8"}/>
        <stop offset="100%" stopColor={ativo ? "#cbd5e1" : "#64748b"}/>
      </linearGradient>
      <linearGradient id="glassM" x1="0" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor={ativo ? "#bfdbfe" : "#475569"} stopOpacity="0.9"/>
        <stop offset="100%" stopColor={ativo ? "#93c5fd" : "#334155"} stopOpacity="0.6"/>
      </linearGradient>
    </defs>
    <ellipse cx="60" cy="53" rx="46" ry="3" fill="rgba(0,0,0,0.25)"/>
    <path d="M10 36 L10 28 Q10 24 14 24 L22 24 Q28 10 40 8 L76 8 Q90 8 96 24 L106 24 Q110 24 110 28 L110 36 Q110 40 106 40 L14 40 Q10 40 10 36 Z" fill="url(#bodyM)"/>
    <path d="M30 24 Q34 11 42 9 L74 9 Q84 9 88 24 Z" fill="url(#glassM)"/>
    <line x1="60" y1="9" x2="60" y2="24" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5"/>
    <line x1="14" y1="33" x2="106" y2="33" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
    <rect x="104" y="26" width="6" height="4" rx="2" fill={ativo ? "#fde68a" : "#4b5563"} opacity="0.9"/>
    <rect x="10" y="27" width="5" height="4" rx="1.5" fill={ativo ? "#fca5a5" : "#374151"} opacity="0.9"/>
    <circle cx="28" cy="40" r="9" fill="#1e293b"/><circle cx="28" cy="40" r="5.5" fill="#334155"/><circle cx="28" cy="40" r="2.5" fill={ativo ? "#e2e8f0" : "#475569"}/>
    <circle cx="88" cy="40" r="9" fill="#1e293b"/><circle cx="88" cy="40" r="5.5" fill="#334155"/><circle cx="88" cy="40" r="2.5" fill={ativo ? "#e2e8f0" : "#475569"}/>
    <path d="M40 24 L40 39" stroke="rgba(0,0,0,0.12)" strokeWidth="1"/>
    <path d="M68 24 L68 39" stroke="rgba(0,0,0,0.12)" strokeWidth="1"/>
    <rect x="50" y="30" width="8" height="2" rx="1" fill="rgba(0,0,0,0.15)"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const CarElite = ({ ativo }) => (
  <svg viewBox="0 0 120 60" width="96" height="46" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bodyE" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={ativo ? "#c7d2fe" : "#818cf8"}/>
        <stop offset="100%" stopColor={ativo ? "#a5b4fc" : "#6366f1"}/>
      </linearGradient>
      <linearGradient id="glassE" x1="0" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor={ativo ? "#e0f2fe" : "#7dd3fc"} stopOpacity="0.9"/>
        <stop offset="100%" stopColor={ativo ? "#bae6fd" : "#38bdf8"} stopOpacity="0.6"/>
      </linearGradient>
    </defs>
    {/* sombra */}
    <ellipse cx="60" cy="57" rx="48" ry="3" fill="rgba(0,0,0,0.22)"/>
    {/* carroceria SUV — mais alta e larga */}
    <path d="M8 38 L8 27 Q8 22 13 22 L20 22 Q26 8 40 6 L78 6 Q94 8 100 22 L107 22 Q112 22 112 27 L112 38 Q112 43 107 43 L13 43 Q8 43 8 38 Z" fill="url(#bodyE)"/>
    {/* teto mais alto e quadrado — estilo SUV */}
    <path d="M28 22 Q32 8 42 7 L76 7 Q86 8 90 22 Z" fill="url(#glassE)"/>
    {/* divisor central janela */}
    <line x1="60" y1="7" x2="60" y2="22" stroke="rgba(0,0,0,0.18)" strokeWidth="1.5"/>
    {/* rack teto */}
    <rect x="34" y="5.5" width="52" height="2" rx="1" fill={ativo ? "#818cf8" : "#4338ca"} opacity="0.7"/>
    {/* farol LED */}
    <rect x="8" y="27" width="9" height="3.5" rx="1.5" fill={ativo ? "#e0f2fe" : "#6b7280"} opacity="0.95"/>
    <rect x="8" y="31.5" width="6" height="1.5" rx="1" fill={ativo ? "#bae6fd" : "#4b5563"} opacity="0.7"/>
    {/* lanterna */}
    <rect x="103" y="27" width="9" height="3.5" rx="1.5" fill={ativo ? "#fca5a5" : "#6b7280"} opacity="0.9"/>
    {/* rodas maiores (SUV) */}
    <circle cx="30" cy="43" r="10" fill="#1e293b"/><circle cx="30" cy="43" r="6" fill="#334155"/><circle cx="30" cy="43" r="2.5" fill={ativo ? "#c7d2fe" : "#6366f1"}/>
    <circle cx="90" cy="43" r="10" fill="#1e293b"/><circle cx="90" cy="43" r="6" fill="#334155"/><circle cx="90" cy="43" r="2.5" fill={ativo ? "#c7d2fe" : "#6366f1"}/>
    {/* detalhe porta */}
    <line x1="44" y1="22" x2="44" y2="42" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
    <line x1="73" y1="22" x2="73" y2="42" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
    {/* maçanetas cromadas */}
    <rect x="50" y="32" width="10" height="2" rx="1" fill="rgba(255,255,255,0.4)"/>
    <rect x="76" y="32" width="10" height="2" rx="1" fill="rgba(255,255,255,0.4)"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const MotoIcon = ({ ativo }) => (
  <svg viewBox="0 0 110 52" width="96" height="46" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="mg1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={ativo?"#38bdf8":"#0ea5e9"}/>
        <stop offset="100%" stopColor={ativo?"#2563eb":"#1d4ed8"}/>
      </linearGradient>
    </defs>
    {/* sombra */}
    <ellipse cx="55" cy="50" rx="38" ry="2" fill="rgba(0,0,0,0.3)"/>
    {/* roda traseira */}
    <circle cx="20" cy="38" r="11" fill="#111827"/>
    <circle cx="20" cy="38" r="7"  fill="#1f2937"/>
    <circle cx="20" cy="38" r="2"  fill={ativo?"#38bdf8":"#0ea5e9"}/>
    <line x1="20" y1="31" x2="20" y2="45" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="13" y1="38" x2="27" y2="38" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="15" y1="33" x2="25" y2="43" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="25" y1="33" x2="15" y2="43" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    {/* roda dianteira */}
    <circle cx="88" cy="38" r="11" fill="#111827"/>
    <circle cx="88" cy="38" r="7"  fill="#1f2937"/>
    <circle cx="88" cy="38" r="2"  fill={ativo?"#38bdf8":"#0ea5e9"}/>
    <line x1="88" y1="31" x2="88" y2="45" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="81" y1="38" x2="95" y2="38" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="83" y1="33" x2="93" y2="43" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    <line x1="93" y1="33" x2="83" y2="43" stroke={ativo?"#38bdf8":"#334155"} strokeWidth="1" opacity="0.6"/>
    {/* garfo dianteiro */}
    <line x1="88" y1="27" x2="78" y2="16" stroke="#475569" strokeWidth="3" strokeLinecap="round"/>
    <line x1="88" y1="27" x2="82" y2="15" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
    {/* braço traseiro */}
    <line x1="20" y1="27" x2="36" y2="20" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/>
    {/* corpo principal — carenagem sportbike */}
    <path d="M34 20 Q44 10 60 9 Q72 8 80 14 L88 27 L78 28 Q68 30 54 30 Q40 30 34 28 Z" fill="url(#mg1)"/>
    {/* carenagem inferior */}
    <path d="M34 26 Q40 30 54 32 Q68 32 78 28 L78 34 Q68 38 50 37 Q32 36 28 32 Q22 28 20 27 L34 20 Z" fill={ativo?"#1d4ed8":"#1e3a8a"} opacity="0.9"/>
    {/* listra decorativa preta */}
    <path d="M44 14 Q60 12 74 17 L74 20 Q60 15 44 17 Z" fill="#000" opacity="0.25"/>
    {/* parabrisa */}
    <path d="M60 9 Q70 5 78 11 L80 14 Q72 8 60 9 Z" fill="#bae6fd" opacity="0.5"/>
    {/* selim */}
    <path d="M38 18 Q52 10 64 11 Q60 14 52 15 Q44 16 38 18 Z" fill="#0f172a" opacity="0.85"/>
    {/* guidão */}
    <line x1="79" y1="14" x2="86" y2="11" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
    <line x1="79" y1="15" x2="87" y2="18" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
    {/* farol */}
    <path d="M84 18 Q91 20 91 25 L88 27 L82 22 Z" fill={ativo?"#fef3c7":"#1e3a5f"}/>
    {ativo && <>
      <line x1="91" y1="21" x2="100" y2="18" stroke="#fef3c7" strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>
      <line x1="91" y1="24" x2="102" y2="23" stroke="#fef3c7" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
    </>}
    {/* farol traseiro */}
    <rect x="7" y="23" width="6" height="3" rx="1.5" fill={ativo?"#f87171":"#450a0a"}/>
    {/* escapamento */}
    <path d="M22 37 Q14 36 11 38" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round"/>
    <ellipse cx="10" cy="38" rx="2" ry="1.5" fill="#475569"/>
  </svg>
);

// ─── ÍCONES ───────────────────────────────────────────────────
const IconClock = ({ color = "rgba(255,255,255,0.3)", size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
  </svg>
);
const IconPin = ({ color = "#ec4899", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconChevron = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconUser = ({ color = "rgba(255,255,255,0.4)", size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconGps = ({ color = "#06b6d4", size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3" fill={color}/>
    <line x1="12" y1="4" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="20"/>
    <line x1="4" y1="12" x2="2" y2="12"/><line x1="22" y1="12" x2="20" y2="12"/>
  </svg>
);

const Spinner = ({ size = 14, color = "#ec4899" }) => (
  <div style={{ width: size, height: size, borderRadius: "50%", border: `2px solid rgba(255,255,255,0.08)`, borderTopColor: color, animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
);

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function EscolhaDestino({ setDestino, agendadoLabel = "Ir agora", paraQuemLabel = "Para mim", onOpenAgendamento, onOpenParaQuem, onEscolherNoMapa, destinoPreSelecionado, onDestinoPreSelecionadoUsado, enderecoGps = "", cidadeAtual = "Jaraguá do Sul", userPosition = null, locationStatus = "loading", onRetryLocation, onSearchInteractionChange }) {
  const [input, setInput] = useState("");
  const [destinationMode, setDestinationMode] = useState(DESTINATION_MODE.SEARCHING);
  const [results, setResults] = useState([]);
  const [origemInput, setOrigemInput] = useState("");
  const [origemResults, setOrigemResults] = useState([]);
  const [origemSelecionada, setOrigemSelecionada] = useState(null); // null = usar GPS
  const [origemFocused, setOrigemFocused] = useState(false);
  const [buscandoOrigem, setBuscandoOrigem] = useState(false);
  const [origemError, setOrigemError] = useState("");

  const [distanciaInfo, setDistanciaInfo] = useState(null);
  const [destinoSelecionado, setDestinoSelecionado] = useState(null);
  const [categoria, setCategoria] = useState(null);
  const [buscandoDestino, setBuscandoDestino] = useState(false);
  const [destinoError, setDestinoError] = useState("");
  const [calculandoRota, setCalculandoRota] = useState(false);
  const [userPos, setUserPos] = useState(
    userPosition || (isStoreScreenshotCapture ? STORE_SCREENSHOT_PUBLIC_ORIGIN : null),
  );
  const [historico, setHistorico] = useState([]);
  const [abaLocais, setAbaLocais] = useState("recentes");
  const [locaisFavoritos, setLocaisFavoritos] = useState([]);
  const [inputFocused, setInputFocused] = useState(false);
  const [tarifaAberta, setTarifaAberta] = useState(null);

  // Verificação de perfil
  const temFoto = !!(localStorage.getItem("usuarioFoto"));
  const temTelefone = (localStorage.getItem("telefone") || "").replace(/\D/g, "").length >= 10;
  const perfilVerificado = temFoto && temTelefone;

  const timerRef = useRef(null);
  const origemTimerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const destinoPlacesSessionRef = useRef(createPlacesSessionToken());
  const origemPlacesSessionRef = useRef(createPlacesSessionToken());
  const inputRef = useRef(null);
  const origemInputRef = useRef(null);

  // Posição efetiva (GPS ou endereço digitado)
  const posEfetiva = origemSelecionada || userPos;
  const addressSearchActive = origemFocused || inputFocused;

  useEffect(() => {
    onSearchInteractionChange?.(addressSearchActive);
    return () => onSearchInteractionChange?.(false);
  }, [addressSearchActive, onSearchInteractionChange]);

  useEffect(() => {
    const hist = JSON.parse(localStorage.getItem("corridasHistorico") || "[]");
    const unicos = []; const vistos = new Set();
    for (const h of hist) {
      if (h.destino && !vistos.has(h.destino) && h.lat && h.lng) { vistos.add(h.destino); unicos.push(h); }
    }
    setHistorico(unicos.slice(0, 5));
    const salvos = [["casa", "Casa"], ["trabalho", "Trabalho"]]
      .map(([key, label]) => {
        try {
          const value = JSON.parse(localStorage.getItem(key) || "null");
          return value?.endereco ? { ...value, label } : null;
        } catch { return null; }
      })
      .filter(Boolean);
    setLocaisFavoritos(salvos);
  }, []);

  useEffect(() => {
    if (userPosition?.lat && userPosition?.lng) setUserPos(userPosition);
  }, [userPosition]);

  useEffect(() => {
    if (isNativeIOSPlaces()) return undefined;
    const init = () => {
      if (!window.google?.maps?.places) return;
      if (!autocompleteServiceRef.current) autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      if (!placesServiceRef.current) { const div = document.createElement("div"); placesServiceRef.current = new window.google.maps.places.PlacesService(div); }
      if (!directionsServiceRef.current) directionsServiceRef.current = new window.google.maps.DirectionsService();
    };
    if (window.google?.maps?.places) { init(); return; }
    const iv = setInterval(() => { if (window.google?.maps?.places) { init(); clearInterval(iv); } }, 500);
    return () => clearInterval(iv);
  }, []);

  // Centro de Jaraguá do Sul — fallback quando GPS não disponível
  // Bias moderado: 25km prioriza resultados próximos mas ainda sugere a cidade
  const _buildParams = useCallback((query) => {
    const center = (posEfetiva?.lat && posEfetiva?.lng) ? posEfetiva : DEFAULT_PLACES_CENTER;
    return {
      input: query,
      componentRestrictions: { country: "br" },
      types: ["establishment", "geocode"],
      locationBias: { center: { lat: center.lat, lng: center.lng }, radius: 25000 },
    };
  }, [posEfetiva]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarGoogle = useCallback((query, callback) => {
    if (!query || query.length < 3) { callback([], ""); return; }
    if (isStoreScreenshotCapture) {
      buscarNominatimParaScreenshot(query)
        .then(items => callback(items.map(item => {
          const parts = String(item.display_name || "").split(",").map(value => value.trim()).filter(Boolean);
          return {
            placeId: `osm-${item.osm_type}-${item.osm_id}`,
            name: parts[0] || item.name || "Destino",
            address: parts.slice(1).join(", "),
            lat: Number(item.lat),
            lng: Number(item.lon),
          };
        }).filter(item => Number.isFinite(item.lat) && Number.isFinite(item.lng)), ""))
        .catch(() => callback([], "Não foi possível buscar endereços. Tente novamente."));
      return;
    }
    if (isNativeIOSPlaces()) {
      const center = (posEfetiva?.lat && posEfetiva?.lng) ? posEfetiva : DEFAULT_PLACES_CENTER;
      nativePlacesAutocomplete(query, center, destinoPlacesSessionRef.current)
        .then(items => {
          const mapped = items.map(item => ({ ...item, lat: null, lng: null }));
          _autocompleteCache.set(`dest_${query}`, mapped);
          callback(mapped, "");
        })
        .catch(() => callback([], "Não foi possível buscar endereços. Tente novamente."));
      return;
    }
    if (!autocompleteServiceRef.current) {
      callback([], "A busca de endereços ainda está carregando. Tente novamente.");
      return;
    }
    const cacheKey = "dest_" + query;
    if (_autocompleteCache.has(cacheKey)) { callback(_autocompleteCache.get(cacheKey), ""); return; }
    autocompleteServiceRef.current.getPlacePredictions(_buildParams(query), (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) { callback([], ""); return; }
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) { callback([], "Não foi possível buscar endereços. Tente novamente."); return; }
      const mapped = predictions.map(p => ({ placeId: p.place_id, name: p.structured_formatting?.main_text || p.description.split(",")[0], address: p.structured_formatting?.secondary_text || p.description, lat: null, lng: null }));
      _autocompleteCache.set(cacheKey, mapped);
      callback(mapped, "");
    });
  }, [_buildParams, posEfetiva]);

  const buscarOrigem = useCallback((query) => {
    if (!query || query.length < 3) { setBuscandoOrigem(false); setOrigemResults([]); return; }
    if (isNativeIOSPlaces()) {
      const center = (posEfetiva?.lat && posEfetiva?.lng) ? posEfetiva : DEFAULT_PLACES_CENTER;
      setBuscandoOrigem(true);
      setOrigemError("");
      nativePlacesAutocomplete(query, center, origemPlacesSessionRef.current)
        .then(items => {
          _autocompleteCache.set(`orig_${query}`, items);
          setOrigemResults(items);
          setOrigemError("");
        })
        .catch(() => {
          setOrigemResults([]);
          setOrigemError("Não foi possível buscar endereços. Tente novamente.");
        })
        .finally(() => setBuscandoOrigem(false));
      return;
    }
    if (!autocompleteServiceRef.current) {
      setBuscandoOrigem(false);
      setOrigemResults([]);
      setOrigemError("A busca de endereços ainda está carregando. Tente novamente.");
      return;
    }
    const cacheKey = "orig_" + query;
    if (_autocompleteCache.has(cacheKey)) { setOrigemResults(_autocompleteCache.get(cacheKey)); setOrigemError(""); setBuscandoOrigem(false); return; }
    setBuscandoOrigem(true);
    setOrigemError("");
    autocompleteServiceRef.current.getPlacePredictions(_buildParams(query), (predictions, status) => {
      setBuscandoOrigem(false);
      if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) { setOrigemResults([]); return; }
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) { setOrigemResults([]); setOrigemError("Não foi possível buscar endereços. Tente novamente."); return; }
      const mapped = predictions.map(p => ({ placeId: p.place_id, name: p.structured_formatting?.main_text || p.description.split(",")[0], address: p.structured_formatting?.secondary_text || p.description }));
      _autocompleteCache.set(cacheKey, mapped);
      setOrigemResults(mapped);
    });
  }, [_buildParams, posEfetiva]);

  // Aplica destino vindo do mapa (pin escolhido)
  useEffect(() => {
    if (!destinoPreSelecionado?.lat || !destinoPreSelecionado?.lng) return;
    setInput(destinoPreSelecionado.nome);
    setResults([]);
    setDestinationMode(DESTINATION_MODE.SELECTED);
    aplicarDestino(destinoPreSelecionado.lat, destinoPreSelecionado.lng, destinoPreSelecionado.nome, destinoPreSelecionado.endereco);
    onDestinoPreSelecionadoUsado?.();
  }, [destinoPreSelecionado]); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca destino
  useEffect(() => {
    if (!shouldFetchDestinationSuggestions(destinationMode, input)) { setResults([]); setBuscandoDestino(false); setDestinoError(""); return undefined; }
    clearTimeout(timerRef.current);
    setBuscandoDestino(true);
    setDestinoError("");
    timerRef.current = setTimeout(() => buscarGoogle(input, (items, error) => {
      setResults(items);
      setDestinoError(error);
      setBuscandoDestino(false);
    }), 300);
    return () => clearTimeout(timerRef.current);
  }, [destinationMode, input, buscarGoogle]);

  // Busca origem
  useEffect(() => {
    if (!origemInput || origemInput.length < 3) { setOrigemResults([]); setBuscandoOrigem(false); setOrigemError(""); return; }
    clearTimeout(origemTimerRef.current);
    setBuscandoOrigem(true);
    setOrigemError("");
    origemTimerRef.current = setTimeout(() => buscarOrigem(origemInput), 300);
    return () => clearTimeout(origemTimerRef.current);
  }, [origemInput, buscarOrigem]);

  function calcularDistanciaReal(destLat, destLng, callback, originOverride = null) {
    const pos = originOverride || posEfetiva;
    if (!pos?.lat || !pos?.lng) { setCalculandoRota(false); onRetryLocation?.(); return; }
    const fallback = () => { const km = haversineKm(pos.lat, pos.lng, destLat, destLng); callback({ km: +km.toFixed(1), minutos: Math.round((km / 30) * 60) }); };
    if (isStoreScreenshotCapture) { fallback(); return; }
    if (!directionsServiceRef.current) { fallback(); return; }
    directionsServiceRef.current.route(
      { origin: { lat: pos.lat, lng: pos.lng }, destination: { lat: destLat, lng: destLng }, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === "OK" && result?.routes?.[0]?.legs?.[0]) {
          const leg = result.routes[0].legs[0];
          callback({ km: +(leg.distance.value / 1000).toFixed(1), minutos: Math.round(leg.duration.value / 60) });
        } else { fallback(); }
      }
    );
  }

  function selecionarOrigem(item) {
    if (isNativeIOSPlaces()) {
      setBuscandoOrigem(true);
      nativePlaceDetails(item.placeId, origemPlacesSessionRef.current)
        .then(place => {
          origemPlacesSessionRef.current = createPlacesSessionToken();
          setOrigemSelecionada({ lat: place.lat, lng: place.lng, name: place.name || item.name, address: place.address || item.address });
          setOrigemInput(place.name || item.name);
          setOrigemResults([]);
          origemInputRef.current?.blur();
          setOrigemFocused(false);
          if (destinoSelecionado) {
            setCalculandoRota(true);
            calcularDistanciaReal(destinoSelecionado.lat, destinoSelecionado.lng, dist => {
              setCalculandoRota(false);
              setDistanciaInfo(dist);
            }, { lat: place.lat, lng: place.lng });
          }
        })
        .catch(() => setOrigemError("Não foi possível abrir este endereço. Tente novamente."))
        .finally(() => setBuscandoOrigem(false));
      return;
    }
    if (!placesServiceRef.current) return;
    setBuscandoOrigem(true);
    placesServiceRef.current.getDetails({ placeId: item.placeId, fields: ["geometry", "name", "formatted_address"] }, (place, status) => {
      setBuscandoOrigem(false);
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setOrigemSelecionada({ lat, lng, name: place.name || item.name, address: place.formatted_address || item.address });
      setOrigemInput(place.name || item.name);
      setOrigemResults([]);
      origemInputRef.current?.blur();
      setOrigemFocused(false);
      // Recalcular rota se destino já está selecionado
      if (destinoSelecionado) {
        setCalculandoRota(true);
        const calcular = (pos) => {
          directionsServiceRef.current?.route(
            { origin: { lat: pos.lat, lng: pos.lng }, destination: { lat: destinoSelecionado.lat, lng: destinoSelecionado.lng }, travelMode: window.google.maps.TravelMode.DRIVING },
            (result, status) => {
              setCalculandoRota(false);
              if (status === "OK" && result?.routes?.[0]?.legs?.[0]) {
                const leg = result.routes[0].legs[0];
                const dist = { km: +(leg.distance.value / 1000).toFixed(1), minutos: Math.round(leg.duration.value / 60) };
                setDistanciaInfo(dist);
              } else { setCalculandoRota(false); }
            }
          );
        };
        calcular({ lat, lng });
      }
    });
  }

  function limparOrigem() {
    setOrigemInput("");
    setOrigemSelecionada(null);
    setOrigemResults([]);
    // Recalcular com GPS se destino selecionado
    if (destinoSelecionado) {
      setCalculandoRota(true);
      calcularDistanciaReal(destinoSelecionado.lat, destinoSelecionado.lng, (dist) => {
        setCalculandoRota(false);
        setDistanciaInfo(dist);
      });
    }
  }

  function selecionarDestino(item) {
    setInput(item.name); setResults([]); setDestinoError("");
    if (item.lat && item.lng) { aplicarDestino(item.lat, item.lng, item.name, item.address || ""); return; }
    if (isNativeIOSPlaces()) {
      setBuscandoDestino(true);
      nativePlaceDetails(item.placeId, destinoPlacesSessionRef.current)
        .then(place => {
          destinoPlacesSessionRef.current = createPlacesSessionToken();
          aplicarDestino(place.lat, place.lng, place.name || item.name, place.address || item.address);
        })
        .catch(() => setDestinoError("Não foi possível abrir este endereço. Tente novamente."))
        .finally(() => setBuscandoDestino(false));
      return;
    }
    if (!placesServiceRef.current) { setDestinoError("Não foi possível abrir este endereço. Tente novamente."); return; }
    setBuscandoDestino(true);
    placesServiceRef.current.getDetails({ placeId: item.placeId, fields: ["geometry", "name", "formatted_address"] }, (place, status) => {
      setBuscandoDestino(false);
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry) {
        setDestinoError("Não foi possível abrir este endereço. Tente novamente.");
        return;
      }
      aplicarDestino(place.geometry.location.lat(), place.geometry.location.lng(), place.name || item.name, place.formatted_address || item.address);
    });
  }

  function aplicarDestino(lat, lng, name, address) {
    inputRef.current?.blur();
    setInputFocused(false);
    setDestinoSelecionado({ lat, lng, name, address }); setDestinationMode(DESTINATION_MODE.SELECTED); setResults([]); setCalculandoRota(true);
    calcularDistanciaReal(lat, lng, (dist) => {
      setCalculandoRota(false); setDistanciaInfo(dist);
    });
  }

  function selecionarDoHistorico(item) {
    setInput(item.destino); setResults([]); setDestinationMode(DESTINATION_MODE.SELECTED);
    setDestinoSelecionado({ lat: item.lat, lng: item.lng, name: item.destino, address: item.address || "" });
    setCalculandoRota(true);
    calcularDistanciaReal(item.lat, item.lng, (dist) => {
      setCalculandoRota(false); setDistanciaInfo(dist);
    });
  }

  const opcoes = distanciaInfo ? getZunoOptions({ distanceKm: distanciaInfo.km, durationMinutes: distanciaInfo.minutos, cidade: cidadeAtual }) : null;
  const fastestCategory = opcoes
    ? ["moto", "movel", "comfort"].reduce((fastest, id) => (
        Number(opcoes[id]?.etaMin ?? Infinity) < Number(opcoes[fastest]?.etaMin ?? Infinity) ? id : fastest
      ), "moto")
    : null;

  async function confirmarCorrida() {
    if (!destinoSelecionado || !opcoes || !categoria) return;
    const precoBase = opcoes[categoria].finalPrice;
    setDestino({
      lat: destinoSelecionado.lat, lng: destinoSelecionado.lng,
      address: destinoSelecionado.address, name: destinoSelecionado.name, categoria,
      // Origem personalizada se definida
      origemPersonalizada: origemSelecionada || null,
      dadosCorrida: { ...opcoes[categoria], finalPrice: precoBase, distanciaKm: distanciaInfo?.km || 0, tempoMin: distanciaInfo?.minutos || 0 }
    });
  }

  const editarDestino = () => {
    setDestinationMode(DESTINATION_MODE.SEARCHING);
    setResults([]);
    setDistanciaInfo(null);
    setDestinoSelecionado(null);
    setCategoria(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const limparDestino = () => { setInput(""); editarDestino(); };

  const origemLabel = origemSelecionada
    ? origemSelecionada.name
    : enderecoGps
      || (isStoreScreenshotCapture ? "Centro, Divinópolis" : "")
      || (locationStatus === "denied"
        ? "Permissão de localização negada"
        : locationStatus === "unavailable"
          ? "Localização indisponível"
          : "Obtendo sua localização...");
  const canRetryLocation = !origemSelecionada && !origemInput
    && (locationStatus === "denied" || locationStatus === "unavailable");
  const activeAddressQuery = origemFocused ? origemInput : input;
  const activeAddressResults = origemFocused ? origemResults : results;
  const activeAddressLoading = origemFocused ? buscandoOrigem : buscandoDestino;
  const activeAddressError = origemFocused ? origemError : destinoError;

  const retryActiveSearch = () => {
    if (activeAddressQuery.trim().length < 3) return;
    if (origemFocused) {
      setBuscandoOrigem(true);
      buscarOrigem(activeAddressQuery);
      return;
    }
    setBuscandoDestino(true);
    buscarGoogle(activeAddressQuery, (items, error) => {
      setResults(items);
      setDestinoError(error);
      setBuscandoDestino(false);
    });
  };

  return (
    <div className={`ride-choice-screen ${categoria ? "has-confirm-cta" : ""}`} data-ready={distanciaInfo && opcoes ? "true" : "false"} data-address-search={addressSearchActive ? "true" : "false"}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        .zd-result:active{background:rgba(255,255,255,0.06)!important}
        .zd-hist:active{background:rgba(255,255,255,0.05)!important}
        .zd-confirm:active{transform:scale(0.98)!important}
      `}</style>

      {/* ── Banner: perfil não verificado ── */}
      {!perfilVerificado && !distanciaInfo && (
        <div className="zuno-profile-alert">
          <span className="zuno-profile-alert-icon"><LuShieldCheck aria-hidden="true" /></span>
          <div className="zuno-profile-alert-copy">
            <p>Perfil incompleto</p>
            <small>
              {!temFoto && !temTelefone ? "Adicione foto e telefone" : !temFoto ? "Adicione uma foto de perfil" : "Adicione seu telefone"} para verificar seu perfil
            </small>
          </div>
          <a className="zuno-profile-alert-action" href="/conta">Verificar <span aria-hidden="true">→</span></a>
        </div>
      )}

      {destinationMode === DESTINATION_MODE.SEARCHING && !input && !distanciaInfo && (
        <div className="zuno-place-tabs" role="tablist" aria-label="Locais sugeridos">
          <button type="button" role="tab" aria-selected={abaLocais === "recentes"} data-active={abaLocais === "recentes"} onClick={() => setAbaLocais("recentes")}>
            <LuClock3 aria-hidden="true" /> Recentes
          </button>
          <button type="button" role="tab" aria-selected={abaLocais === "favoritos"} data-active={abaLocais === "favoritos"} onClick={() => setAbaLocais("favoritos")}>
            <LuStar aria-hidden="true" /> Favoritos
          </button>
        </div>
      )}

      {/* ── Botões topo ── */}
      <div className="zuno-trip-options" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { label: agendadoLabel, ativo: agendadoLabel !== "Ir agora", icon: <IconClock color={agendadoLabel !== "Ir agora" ? "#a855f7" : "rgba(255,255,255,0.4)"} size={12}/>, onClick: () => onOpenAgendamento?.() },
          { label: paraQuemLabel, ativo: paraQuemLabel !== "Para mim", icon: <IconUser color={paraQuemLabel !== "Para mim" ? "#a855f7" : "rgba(255,255,255,0.4)"} size={12}/>, onClick: () => onOpenParaQuem?.() },
        ].map(({ label, ativo, icon, onClick }, i) => (
          <button key={i} onClick={onClick} style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "9px 12px", borderRadius: 20, border: ativo ? "1px solid rgba(168,85,247,0.35)" : "1px solid rgba(255,255,255,0.1)", background: ativo ? "rgba(168,85,247,0.1)" : "rgba(255,255,255,0.05)", color: ativo ? "#a855f7" : "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", transition: "all 0.2s" }}>
            {icon}<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 10, flexShrink: 0 }}>▾</span>
          </button>
        ))}
      </div>

      {/* ── CAMPO ORIGEM (editável) ── */}
      <div className="zuno-route-search" style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 2px", marginBottom: 6 }}>
          <div style={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%",
              background: origemSelecionada ? "linear-gradient(135deg,#ec4899,#7c3aed)" : "linear-gradient(135deg,#06b6d4,#3b82f6)",
              boxShadow: origemSelecionada ? "0 0 6px rgba(236,72,153,0.5)" : "0 0 6px rgba(6,182,212,0.5)" }} />
          </div>
          <div className="zuno-route-field" data-focused={origemFocused} data-accent="origin" aria-busy={locationStatus === "loading"} style={{ flex: 1 }}>
            {!origemFocused && !origemInput && (
              <span style={{ marginRight: 6, flexShrink: 0 }}>
                <IconGps />
              </span>
            )}
            <input
              ref={origemInputRef}
              type="text"
              value={origemInput}
              onChange={e => { setOrigemInput(e.target.value); if (!e.target.value) { setOrigemSelecionada(null); if (destinoSelecionado) { setCalculandoRota(true); calcularDistanciaReal(destinoSelecionado.lat, destinoSelecionado.lng, (dist) => { setCalculandoRota(false); setDistanciaInfo(dist); }); } } }}
              onFocus={() => setOrigemFocused(true)}
              onBlur={() => setTimeout(() => setOrigemFocused(false), 200)}
              placeholder={origemLabel}
              style={{ flex: 1 }}
            />
            {(buscandoOrigem || (locationStatus === "loading" && !origemInput)) && <Spinner size={12} color="#06b6d4" />}
            {canRetryLocation && (
              <button type="button" className="zuno-location-retry" onClick={onRetryLocation}>Tentar</button>
            )}
            {origemInput && !buscandoOrigem && (
              <button aria-label="Limpar origem" onClick={limparOrigem} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "rgba(255,255,255,0.3)", display:"flex" }}><LuX /></button>
            )}
          </div>
        </div>

        {/* Linha conectora */}
        <div style={{ width: 1.5, height: 10, background: "rgba(255,255,255,0.08)", marginLeft: 11, marginBottom: 6 }} />

        {/* ── CAMPO DESTINO ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 2px" }}>
          <div style={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ width: 10, height: 10, background: "#ec4899", borderRadius: 3, boxShadow: "0 0 6px rgba(236,72,153,0.6)" }} />
          </div>
          {destinationMode === DESTINATION_MODE.SELECTED && destinoSelecionado ? (
            <button type="button" className="zuno-selected-destination" onClick={editarDestino} aria-label="Editar destino selecionado">
              <span>
                <strong>{destinoSelecionado.name}</strong>
                <small>{destinoSelecionado.address || "Destino selecionado"}</small>
              </span>
              <span className="zuno-selected-destination-edit">Editar</span>
            </button>
          ) : (
            <div className="zuno-route-field" data-focused={inputFocused} data-accent="destination" style={{ flex: 1 }}>
              <input ref={inputRef} type="text" value={input} aria-label="Destino da corrida"
                onChange={e => { setInput(e.target.value); setResults([]); }}
                onFocus={() => setInputFocused(true)}
                onBlur={() => isStoreScreenshotCapture
                  ? setTimeout(() => setInputFocused(false), 250)
                  : setInputFocused(false)}
                placeholder="Para onde vamos?" autoFocus
                style={{ flex: 1 }} />
              {(buscandoDestino || calculandoRota) && <Spinner />}
              {input && !buscandoDestino && !calculandoRota && (
                <button aria-label="Limpar destino" onClick={limparDestino} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex", alignItems: "center", color: "rgba(255,255,255,0.3)" }}><LuX /></button>
              )}
            </div>
          )}
        </div>
      </div>

      {addressSearchActive && (
        <div className="zuno-address-results" role="listbox" aria-label={origemFocused ? "Sugestões de embarque" : "Sugestões de destino"} aria-busy={activeAddressLoading}>
          {activeAddressQuery.trim().length < 3 && (
            <div className="zuno-address-state">Digite pelo menos 3 caracteres para buscar.</div>
          )}
          {activeAddressQuery.trim().length >= 3 && activeAddressLoading && (
            <div className="zuno-address-state"><Spinner size={15} color="#06b6d4" /> Buscando endereços...</div>
          )}
          {activeAddressQuery.trim().length >= 3 && !activeAddressLoading && activeAddressError && (
            <div className="zuno-address-state zuno-address-state-error">
              <span>{activeAddressError}</span>
              <button type="button" onClick={retryActiveSearch}>Tentar novamente</button>
            </div>
          )}
          {activeAddressQuery.trim().length >= 3 && !activeAddressLoading && !activeAddressError && activeAddressResults.length === 0 && (
            <div className="zuno-address-state">Nenhum endereço encontrado.</div>
          )}
          {!activeAddressLoading && !activeAddressError && activeAddressResults.map((result) => (
            <button
              type="button"
              role="option"
              aria-selected="false"
              aria-label={`${result.name}, ${result.address}`}
              key={result.placeId || `${result.name}-${result.address}`}
              className="zuno-address-result zd-result"
              onPointerDown={(event) => { if (!isStoreScreenshotCapture) event.preventDefault(); }}
              onClick={() => origemFocused ? selecionarOrigem(result) : selecionarDestino(result)}
            >
              <span className="zuno-address-result-icon">
                {origemFocused ? <IconGps color="#06b6d4" size={13} /> : <IconPin color="#06b6d4" size={15} />}
              </span>
              <span className="zuno-address-result-copy">
                <strong>{result.name}</strong>
                <small>{result.address}</small>
              </span>
              <IconChevron />
            </button>
          ))}
        </div>
      )}

      {calculandoRota && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "rgba(6,182,212,0.07)", border: "1px solid rgba(6,182,212,0.15)", borderRadius: 10, marginTop: 8, animation: "fadeIn 0.3s ease" }}>
          <Spinner size={12} color="#06b6d4" />
          <span style={{ fontSize: 12, color: "#06b6d4" }}>Calculando rota real...</span>
        </div>
      )}


      {/* ── Escolher no mapa ── */}
      {!addressSearchActive && destinationMode === DESTINATION_MODE.SEARCHING && !distanciaInfo && onEscolherNoMapa && (
        <div className="zuno-map-choice-wrap">
          <button type="button" onClick={onEscolherNoMapa} className="zuno-map-choice zd-hist">
            <span className="zuno-map-choice-icon"><LuMapPin aria-hidden="true" /></span>
            <span className="zuno-map-choice-copy">
              <strong>Escolher no mapa</strong>
              <small>Arraste o mapa e fixe o pino no local</small>
            </span>
            <span className="zuno-map-choice-chevron" aria-hidden="true">›</span>
          </button>
        </div>
      )}

      {/* ── Histórico ── */}
      {!addressSearchActive && abaLocais === "recentes" && destinationMode === DESTINATION_MODE.SEARCHING && !input && !distanciaInfo && historico.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="zuno-history-card" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, overflow: "hidden" }}>
            {historico.map((item, i) => (
              <div key={i} className="zd-hist" onClick={() => selecionarDoHistorico(item)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", borderBottom: i < historico.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none", transition: "background 0.15s" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <IconClock color="rgba(255,255,255,0.3)" size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: 13, color: "#fff", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.destino}</p>
                  {item.address && <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.address}</p>}
                </div>
                <IconChevron />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Resultados destino ── */}
      {/* ── Cards categorias ── */}
      {distanciaInfo && opcoes && (
        <div className="zuno-ready-ride" style={{ animation: "fadeIn 0.35s ease" }}>
          {/* Info rota */}
          <div className="zuno-route-summary">
            <div className="zuno-route-points" aria-hidden="true">
              <i />
              <span />
              <i />
            </div>
            <div className="zuno-route-addresses">
              <button type="button" onClick={() => { setDistanciaInfo(null); requestAnimationFrame(() => document.querySelector('.zuno-route-field[data-accent="origin"] input')?.focus()); }}>
                <span><small>Embarque</small><strong>{origemSelecionada ? origemSelecionada.name : origemLabel}</strong></span>
                <LuChevronRight aria-hidden="true" />
              </button>
              <button type="button" onClick={editarDestino}>
                <span><small>Destino</small><strong>{destinoSelecionado?.name}</strong></span>
                <LuChevronRight aria-hidden="true" />
              </button>
            </div>
            <div className="zuno-route-metrics" aria-label={`${distanciaInfo.km} quilômetros, aproximadamente ${distanciaInfo.minutos} minutos`}>
              <strong>{distanciaInfo.km} km</strong>
              <span>~{distanciaInfo.minutos} min</span>
            </div>
          </div>

          {/* Banner surge — aparece quando há alta demanda */}
          {opcoes?.movel?.isSurge && (
            <div className="zuno-surge-notice">
              <LuZap aria-hidden="true" />
              <div>
                <strong>Alta demanda agora — {opcoes.movel.surgeLabel}</strong>
                <span>Preços temporariamente mais altos.</span>
              </div>
            </div>
          )}

          {[
            { id: "moto",    label: "Zuno Moto",  sub: "Mototáxi • Rápido e econômico"         },
            { id: "movel",   label: "Zuno Go",    sub: "Carro • Rápido e acessível"             },
            { id: "comfort", label: "Zuno Elite", sub: "Carro premium • Mais espaço e conforto" },
          ].map(({ id, label, sub }) => {
            const data = opcoes[id];
            if (!data) return null;
            const ativo = categoria === id;
            const maisRapido = id === fastestCategory;
            return (
              <div key={id} className="zuno-category-card" data-active={ativo ? "true" : "false"} role="button" tabIndex={0} aria-label={label} aria-pressed={ativo} onClick={() => setCategoria(id)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setCategoria(id); } }}
              >
                <div className="zuno-category-vehicle">
                  <ZunoVehicle type={id} />
                  {maisRapido && <span className="zuno-fastest-badge">Mais rápido</span>}
                </div>
                <div className="zuno-category-copy">
                  <div className="zuno-category-heading">
                    <strong>{label}</strong>
                    <button type="button" onClick={event => { event.stopPropagation(); setTarifaAberta({ label, data }); }} aria-label={`Ver detalhes da tarifa ${label}`} title="Ver composição da tarifa"><LuInfo /></button>
                  </div>
                  <span>{sub}</span>
                  <small><IconClock /> {data.etaMin}–{data.etaMax} min · {distanciaInfo.km} km</small>
                </div>
                <div className="zuno-category-price">
                  {data.isSurge && <span><LuZap /> Alta demanda</span>}
                  <strong>R$ {data.finalPrice.toFixed(2)}</strong>
                  <i aria-hidden="true">{ativo && <LuCheck />}</i>
                </div>
              </div>
            );
          })}

          <div className="zuno-payment-next" aria-label="Pagamento escolhido na próxima etapa">
            <span aria-hidden="true"><LuCreditCard /></span>
            <div><strong>Pagamento</strong><small>Você escolhe na próxima etapa</small></div>
            <LuChevronRight aria-hidden="true" />
          </div>

        </div>
      )}

      {!addressSearchActive && abaLocais === "favoritos" && destinationMode === DESTINATION_MODE.SEARCHING && !input && !distanciaInfo && (
        <div className="zuno-favorite-places" role="tabpanel">
          {locaisFavoritos.length > 0 ? locaisFavoritos.map((item) => (
            <button key={item.label} type="button" onClick={() => {
              setInput(item.endereco);
              setDestinationMode(DESTINATION_MODE.SEARCHING);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}>
              <LuStar aria-hidden="true" />
              <span><strong>{item.label}</strong><small>{item.endereco}</small></span>
              <i aria-hidden="true">›</i>
            </button>
          )) : <p>Nenhum local favorito salvo ainda.</p>}
        </div>
      )}

      {opcoes && categoria && (
        <div className="zuno-ride-confirm-bar">
          <button className="zd-confirm" onClick={confirmarCorrida}>
            Confirmar {categoria === "moto" ? "Zuno Moto" : categoria === "comfort" ? "Zuno Elite" : "Zuno Go"} · R$ {opcoes[categoria].finalPrice.toFixed(2)}
          </button>
        </div>
      )}

      {tarifaAberta && (
        <div role="presentation" onClick={() => setTarifaAberta(null)} style={{position:"fixed",inset:0,zIndex:10020,display:"flex",alignItems:"flex-end",justifyContent:"center",background:"rgba(4,8,18,.72)",backdropFilter:"blur(8px)"}}>
          <section role="dialog" aria-modal="true" aria-labelledby="tarifa-title" onClick={event => event.stopPropagation()} style={{width:"min(520px,100%)",padding:"22px 24px max(30px,env(safe-area-inset-bottom))",borderRadius:"28px 28px 0 0",border:"1px solid #25314d",background:"#0d1428",boxShadow:"0 -24px 70px rgba(0,0,0,.5)"}}>
            <div style={{width:38,height:4,borderRadius:4,background:"#4a5670",margin:"0 auto 22px"}} />
            <h2 id="tarifa-title" style={{margin:"0 0 8px",fontSize:22,color:"#f8faff"}}>Tarifa {tarifaAberta.label}</h2>
            <p style={{margin:"0 0 20px",color:"#9aa4b8",fontSize:13,lineHeight:1.6}}>O total usa os dados reais da rota e pode variar conforme tempo, distância, demanda e taxas aplicáveis.</p>
            {[
              ["Tarifa base", tarifaAberta.data.baseFare],
              ["Por quilômetro", tarifaAberta.data.perKm],
              ["Por minuto", tarifaAberta.data.perMin],
            ].map(([nome, valor]) => (
              <div key={nome} style={{display:"flex",justifyContent:"space-between",padding:"13px 0",borderBottom:"1px solid #25314d",color:"#9aa4b8",fontSize:14}}>
                <span>{nome}</span><strong style={{color:"#f8faff"}}>R$ {Number(valor || 0).toFixed(2)}</strong>
              </div>
            ))}
            <button onClick={() => setTarifaAberta(null)} style={{width:"100%",minHeight:54,marginTop:20,border:0,borderRadius:16,background:"var(--zuno-gradient)",color:"#fff",fontWeight:800,cursor:"pointer"}}>Entendi</button>
          </section>
        </div>
      )}
    </div>
  );
}

export default EscolhaDestino;
